// Home v2 — network filter dropdown (SHARED kit). Used by the mobile-web
// dashboard (AssetTable toolbar) + Activity screen AND the extension popup/tab
// dashboard so the "All Networks" filter is one inline dropdown (pill trigger +
// card menu) instead of a separate route or the popup-style generic Select.
// Caller owns the option list + selection state; this is the presentational
// shell. Closes on outside press.
import React, { useCallback, useRef, useState } from 'react'
import { Platform, Pressable, ScrollView, View } from 'react-native'

import NetworksIcon from '@common/assets/svg/NetworksIcon'
import NetworkIcon from '@common/components/NetworkIcon'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'

import { ChevronDownIcon } from './HomeIcons'
import { RADII } from './tokens'

// A transformed RN-Web ScrollView ancestor becomes the containing block for a
// `position: fixed` menu, trapping it inside the card (painted under sibling
// columns). Portaling to <body> escapes that ancestor so the fixed coords are
// truly viewport-relative. Web-only; native keeps the inline menu.
const createPortal: ((node: React.ReactNode, container: Element) => React.ReactNode) | null =
  Platform.OS === 'web' ? require('react-dom').createPortal : null
const toBody = (node: React.ReactNode): React.ReactNode =>
  createPortal && typeof document !== 'undefined' ? createPortal(node, document.body) : node

export type NetworkFilterOption = {
  /** 'all' | chainId string | network name — caller's key. */
  value: string
  label: string
  /** NetworkIcon id (chainId / 'gasTank' / 'rewards'). Omit → "all networks" grid icon. */
  iconId?: string
}

type Props = {
  options: NetworkFilterOption[]
  value: string
  onSelect: (value: string) => void
  /** Trigger + menu min width. */
  minWidth?: number
  /** Shrink height/padding/icon/text for tight mobile toolbars. */
  compact?: boolean
  testID?: string
}

const OptionIcon: React.FC<{ iconId?: string; size?: number }> = ({ iconId, size = 20 }) => {
  if (!iconId) return <NetworksIcon width={size} height={size} />
  return <NetworkIcon id={iconId} size={size} />
}

const NetworkFilterDropdown: React.FC<Props> = ({
  options,
  value,
  onSelect,
  minWidth,
  compact,
  testID
}) => {
  const { theme } = useTheme()
  const [open, setOpen] = useState(false)
  // Viewport-anchored menu position — rendered `position: fixed` so it escapes the
  // toolbar/ScrollView stacking context (an `absolute` menu was painted UNDER the
  // dashboard's right column, a later DOM sibling).
  const [anchor, setAnchor] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const triggerRef = useRef<View>(null)
  const current = options.find((o) => o.value === value) ?? options[0]
  // Non-default selection (a specific network) gets the accent treatment.
  const active = !!current?.iconId

  const triggerHeight = compact ? 30 : 36
  const triggerPadH = compact ? 8 : 12
  const iconSize = compact ? 16 : 20
  const fontSize = compact ? 12 : 14
  const gap = compact ? 6 : 8
  const resolvedMinWidth = minWidth ?? (compact ? 110 : 200)

  const toggle = useCallback(() => {
    if (open) {
      setOpen(false)
      return
    }
    triggerRef.current?.measureInWindow((x, y, _w, h) => {
      setAnchor({ top: y + h + 6, left: x })
      setOpen(true)
    })
  }, [open])

  return (
    <View style={{ position: 'relative', zIndex: 20 }}>
      <Pressable
        ref={triggerRef}
        testID={testID}
        onPress={toggle}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap,
          minWidth: resolvedMinWidth,
          height: triggerHeight,
          paddingHorizontal: triggerPadH,
          borderRadius: RADII.pill,
          backgroundColor: (active ? theme.infoBackground : theme.homeControlBackground) as string,
          borderWidth: 1,
          borderColor: (active || open ? theme.primary : theme.homeCardBorder) as string
        }}
      >
        <OptionIcon iconId={current?.iconId} size={iconSize} />
        <Text fontSize={fontSize} weight="semiBold" numberOfLines={1} style={{ flex: 1 }}>
          {current?.label}
        </Text>
        <ChevronDownIcon size={compact ? 14 : 16} color={theme.secondaryText as string} />
      </Pressable>

      {open && toBody(
        <>
          {/* outside-press catcher — frosted backdrop so the page dims/blurs behind the menu */}
          <Pressable
            onPress={() => setOpen(false)}
            style={
              {
                position: 'fixed',
                inset: 0,
                zIndex: 9998,
                backgroundColor: 'rgba(0,0,0,0.2)',
                backdropFilter: 'blur(3px)'
              } as any
            }
          />
          <View
            style={
              {
                position: 'fixed',
                top: anchor.top,
                left: anchor.left,
                minWidth: resolvedMinWidth,
                maxHeight: 320,
                zIndex: 9999,
                backgroundColor: theme.homeCardBackground as string,
                borderWidth: 1,
                borderColor: theme.homeCardBorder as string,
                borderRadius: RADII.md,
                paddingVertical: 4,
                overflow: 'hidden',
                boxShadow: '0 12px 32px rgba(20,24,51,0.12)'
              } as any
            }
          >
            <ScrollView showsVerticalScrollIndicator={false}>
              {options.map((opt) => {
                const isActive = opt.value === value
                return (
                  <Pressable
                    key={opt.value}
                    onPress={() => {
                      onSelect(opt.value)
                      setOpen(false)
                    }}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: compact ? 8 : 10,
                      paddingVertical: compact ? 8 : 10,
                      paddingHorizontal: compact ? 10 : 12,
                      backgroundColor: isActive
                        ? (theme.quaternaryBackground as string)
                        : 'transparent'
                    }}
                  >
                    <OptionIcon iconId={opt.iconId} size={iconSize} />
                    <Text
                      fontSize={fontSize}
                      weight={isActive ? 'semiBold' : 'regular'}
                      numberOfLines={1}
                    >
                      {opt.label}
                    </Text>
                  </Pressable>
                )
              })}
            </ScrollView>
          </View>
        </>
      )}
    </View>
  )
}

export default React.memo(NetworkFilterDropdown)
